import type {
  ConversationNameEvent,
  ErrorEvent,
  StreamEvent,
  ToolCallEvent,
  ModelChatEvent,
  UserChatEvent,
  WarningEvent,
  CancelledEvent,
  MessagePersistedEvent,
  ModelThinkingEvent,
} from "~/components/assistant/lib/events";
import type {
  ProcessingOptions,
  ProcessingResult,
} from "~/components/assistant/lib/messageTypeRegistry";
import {
  processCancelledMessage,
  processErrorMessage,
  processMessagePersisted,
  processModelMessage,
  processThinkingMessage,
  processToolMessage,
  processUserMessage,
  processWarningMessage,
} from "~/components/assistant/lib/processors";
import type { Message } from "~/components/assistant/types";

// ============================================================================
// Main Router
// ============================================================================

/**
 * Main stream event processor.
 * Delegates to specific processors based on event kind.
 */
export function processStreamEvent(
  messages: Message[],
  event: StreamEvent | null | undefined,
  options: ProcessingOptions
): ProcessingResult | null {
  if (!event) {
    return null;
  }

  // Ignore heartbeat messages - they're only for keeping the connection alive
  if (event.kind === "heartbeat") {
    return null;
  }

  // Handle conversation_name events - auto-generated conversation titles
  if (event.kind === "conversation_name") {
    const nameEvent = event as ConversationNameEvent;
    return {
      messages,
      conversationName: {
        conversationId: nameEvent.conversation_id || "",
        displayName: nameEvent.display_name || "",
      },
    };
  }

  // Handle error events (both old format with event.error and new format with kind="error")
  if ("error" in event || event.kind === "error") {
    return processErrorMessage(messages, event as ErrorEvent, options);
  }

  // Handle warning events
  if (event.kind === "warning") {
    return processWarningMessage(messages, event as WarningEvent, options);
  }

  // Handle cancelled events (backend ACK for cancellation)
  if (event.kind === "cancelled") {
    return processCancelledMessage(messages, event as CancelledEvent, options);
  }

  // Handle message_persisted events - update message ID and enable feedback
  if (event.kind === "message_persisted") {
    return processMessagePersisted(messages, event as MessagePersistedEvent, options);
  }

  // Handle user chat events
  if (event.kind === "user_chat") {
    return processUserMessage(messages, event as UserChatEvent, options);
  }

  // Handle model chat events
  if (event.kind === "model_chat") {
    return processModelMessage(messages, event as ModelChatEvent, options);
  }

  // Handle model thinking events
  if (event.kind === "model_thinking") {
    return processThinkingMessage(messages, event as ModelThinkingEvent, options);
  }

  // Handle tool events
  if (event.kind === "tool_call" || event.kind === "tool_return") {
    return processToolMessage(messages, event as ToolCallEvent, options);
  }

  return null;
}
